import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from 'react-query';
import { Link, useParams } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useTableCurrentState, { ITableColumnState } from 'hooks/useTableCurrentState';

import { useClusterRCFAnalysisStore } from 'store/clusterRCFAnalysis.store';

import { useMutationResetCluster } from 'api/mutations/resetCluster.mutation';
import { QueryResponse } from 'api/queries/clustersData.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { InputOption } from 'components/common/MyCustomSelect/components/InputOption/InputOption';
import { ValueContainer } from 'components/common/MyCustomSelect/components/ValueContainer/ValueContainer';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { WidgetButton } from 'components/Dashboards/Reporting/_common/widgetButtons/WidgetButton';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import { ConfirmSaveModal } from 'components/common';
import { CompareOptions, compareOptions } from 'components/pages/DurationForecastPage/components/CompareTo/CompareTo';
import classes from 'components/pages/DurationForecastPage/components/GraphComponents/components/ClusterActivities/ClusterActivities.module.scss';
import {
    TOtherSelectOptions,
    TStatusSelectOptions,
    columns,
    getFilteredRows,
    getLinkToBattleCards,
    initialState as initialStateMain,
    otherSelectOptions,
    statusSelectOptions,
} from 'components/pages/DurationForecastPage/components/GraphComponents/components/ClusterActivities/ClusterActivities.utils';
import { FilterWarningIcon } from 'components/pages/DurationForecastPage/components/GraphComponents/components/ClusterActivities/components/FilterWarningIcon/FilterWarningIcon';

interface Props {
    tableData?: QueryResponse['data']['clusterActivities'];
    tooltipArg?: IToolTip;
    idList?: number[];
    compare: CompareOptions;
    subTitle: string;
    totalActivities: QueryResponse['statistics']['totalActivities'] | undefined;
}

const ClusterActivities = ({ tableData, tooltipArg, idList = [], compare, subTitle, totalActivities }: Props) => {
    const [isInstance, setIsInstance] = useState(false);
    const tableRef = useRef(null);
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { handleSnackBar } = useCustomSnackBar();
    const selectedClusterId = useClusterRCFAnalysisStore((store) => store.selectedClusterId);
    const searchWord = useClusterRCFAnalysisStore((store) => store.searchWord);
    const isAutomatedCluster = useClusterRCFAnalysisStore((store) => store.isAutomatedClusteringOption);
    const compareKey = compareOptions.find((item) => item.value === compare)?.value;
    const [status, setStatus] = useState<readonly ISelectOption<TStatusSelectOptions>[]>(statusSelectOptions);
    const [otherOptions, setOtherOptions] = useState<readonly ISelectOption<TOtherSelectOptions>[]>([]);
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(false);
    const [modalConfirmOpen, setModalConfirmOpen] = useState<boolean>(false);
    const { mutate: mutationResetCluster } = useMutationResetCluster();
    const queryClient = useQueryClient();
    const { id: versionId } = useParams<ProgramStatusUrlParams>();

    const handleStatus = useCallback((value: readonly ISelectOption<TStatusSelectOptions>[]) => {
        setStatus(value);
        sendEventWithDimensions({
            category: 'Activities Table(s)',
            action: 'Status Filter click',
            label: value.length > 0 ? value.map((i) => i.label).join(',') : 'Deselect All',
        });
    }, []);

    const handleOtherOptions = useCallback((value: readonly ISelectOption<TOtherSelectOptions>[]) => {
        setOtherOptions(value);

        sendEventWithDimensions({
            category: 'Activities Table(s)',
            action: 'Other Options click',
            label: value.length > 0 ? value.map((i) => i.label).join(',') : 'Deselect All',
        });
    }, []);

    const filteredRows = useMemo(() => {
        return tableData
            ? getFilteredRows({
                  rows: tableData,
                  statusFilter: status.map((i) => i.value),
                  otherFilter: isCriticalPath
                      ? [...otherOptions, { value: 'CriticalPath', label: 'Critical Path' }].map((i) => i.value)
                      : otherOptions.map((i) => i.value),
              })
            : [];
    }, [tableData, status, otherOptions, isCriticalPath]);

    const initialState: ITableColumnState = useMemo(() => {
        let hiddenColumns = initialStateMain.hiddenColumns;
        switch (compare) {
            case 'ITSELF':
                hiddenColumns = [...hiddenColumns, 'projectName', 'contractName', 'versionName'];
                break;
            case 'PROJECT':
                hiddenColumns = [...hiddenColumns, 'projectName'];
                break;
            case 'PORTFOLIO':
                hiddenColumns = [...hiddenColumns];
                break;
            default:
                hiddenColumns = [...hiddenColumns];
        }

        return {
            hiddenColumns,
            sortBy: initialStateMain.sortBy,
        };
    }, [idList, compare]);

    const componentKey = 'clusterActivities';
    const updatedInitialState = useTableCurrentState({ initialState, componentKey });
    const showTotalActivities = Boolean(totalActivities && filteredRows.length < totalActivities);
    const title = `Tasks in Cluster (${showTotalActivities ? totalActivities : filteredRows.length})`;

    const handleInstance = (instance) => {
        if (instance) {
            tableRef.current = instance;
            setIsInstance(true); // need for render component after getting instance
        }
    };
    const handleCriticalPath = (event) => {
        const isChecked = event.target.checked;
        setIsCriticalPath(isChecked);
    };
    const handleResetCluster = () => {
        if (selectedClusterId?.clusterId && compareKey) {
            mutationResetCluster(
                { clusterId: selectedClusterId?.clusterId, clusterType: compareKey },
                {
                    onSuccess: () => {
                        return queryClient.invalidateQueries('clustersData').then(() => {
                            handleSnackBar('Cluster tasks were updated to initial state', 'success');
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
        setModalConfirmOpen(false);
    };

    const linkToBattlecards = getLinkToBattleCards(isAutomatedCluster, selectedClusterId, versionId, searchWord);

    return (
        <WidgetWithTitle
            title={
                <WidgetTitle
                    title={title}
                    postfix={
                        <FilterWarningIcon
                            showTotalActivities={showTotalActivities}
                            filteredRowsLength={filteredRows.length}
                        />
                    }
                />
            }
            tooltip={tooltipArg}
            titleComponents={[
                <div data-testid={'link-container'} className={classes.linkContainer}>
                    <Link to={linkToBattlecards}>View in {executionConfig.battlecards.title}</Link>
                </div>,
                isInstance && (
                    <ExportDropdown
                        key={'export'}
                        title={title}
                        data={filteredRows}
                        tableInstance={tableRef.current}
                        subTitle={subTitle}
                    />
                ),
                tableRef.current && (
                    <ColumnsManager
                        onOpenModal={() => setModalColumnsOpen(true)}
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        tableInstance={tableRef.current}
                        initialState={initialState}
                        columns={columns}
                        componentKey={componentKey}
                    />
                ),
                <WidgetButton
                    icon={'settings_backup_restore'}
                    onClick={() => setModalConfirmOpen(true)}
                    tooltipContent={'Reset Cluster'}
                />,
            ]}
            titleFilters={
                <div data-testid={'filter-container'} className={classes.filterContainer}>
                    <div className={classes.filterWrapper}>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>Status</div>
                            <Select<ISelectOption<TStatusSelectOptions>, true>
                                value={status}
                                options={statusSelectOptions}
                                onChange={(value) => handleStatus(value)}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isMulti={true}
                                placeholder={'Select Status...'}
                                components={{
                                    Option: InputOption,
                                    ValueContainer: ValueContainer,
                                }}
                            />
                        </div>
                        <div>
                            <Switch
                                onChange={handleCriticalPath}
                                checked={isCriticalPath}
                                label={'Critical Path Only'}
                                labelPlacement={'start'}
                            />
                        </div>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>More filters</div>
                            <Select<ISelectOption<TOtherSelectOptions>, true>
                                value={otherOptions}
                                options={otherSelectOptions}
                                onChange={(value) => handleOtherOptions(value)}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isMulti={true}
                                placeholder={'Select More Filters...'}
                                components={{
                                    Option: InputOption,
                                    ValueContainer: ValueContainer,
                                }}
                            />
                        </div>
                    </div>
                </div>
            }
        >
            {tableData && (
                <div data-testid={'table-container'} className={classes.tableContainer}>
                    {updatedInitialState && (
                        <div className={classes.tableContainer}>
                            <MainTable
                                getInstanceCallback={handleInstance}
                                columns={columns}
                                data={filteredRows}
                                initialState={updatedInitialState}
                                pageSize={1000}
                            />
                        </div>
                    )}

                    <ConfirmSaveModal
                        isShowModal={modalConfirmOpen}
                        onHideModal={() => setModalConfirmOpen(false)}
                        onConfirm={handleResetCluster}
                        actionButtonLabel={'Yes'}
                    >
                        Are you sure you want to reset this cluster to its original tasks set?
                    </ConfirmSaveModal>
                </div>
            )}

            {!tableData && (
                <div className={classes.boxWrapper}>
                    <p>Waiting for data...</p>
                </div>
            )}
        </WidgetWithTitle>
    );
};

export default ClusterActivities;
